import React, {
    useState, useEffect, useRef, forwardRef,
    createContext, useContext
} from 'react'
import { cls } from 'utilities/dom'
import {
    NavigationList, NavigationItem, Link,
    Section
} from './s'
import Ellipsify from 'components/ellipsify'

const Navigation = ({ navigationItems = [], salt, ...props }) => {
    const [active, setActive] = useState(navigationItems[0]?.id)
    const isProgrammaticScroll = useRef(false)
    const supportsScrollEnd = 'onscrollend' in window

    const hasStickyMenu = !!document.querySelector('.sticky-menu')
    const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--huma-global-main-layout-header-height').replace('px', ''))
    const stickyMenuOffset = hasStickyMenu ? parseInt(getComputedStyle(document.documentElement).getPropertyValue('--huma-global-sticky-menu-height').replace('px', '')) : 0

    useIntersectionObserver({
        setActive,
        rootMarginOffset: headerOffset + stickyMenuOffset,
        navigationItems,
        isProgrammaticScroll,
        headerOffset,
        stickyMenuOffset
    })

    if(!navigationItems.length) {
        return null
    }

    return (
        <NavigationList
            {...props}
            className={cls([
                props.className,
                isProgrammaticScroll.current && 'programmatic-scroll'
            ])}
            style={{
                '--__huma-component-navigation-active-multiplier': navigationItems.findIndex(item => item.id === active),
                '--__huma-component-navigation-offset': `calc(${headerOffset}px + ${stickyMenuOffset}px)`
            }}>
            {navigationItems.map(({ id, text }) => (
                <NavigationItem key={`${salt}:navigation:${id}`}>
                    <Link
                        href={`#${id}`}
                        className={(active === id)}
                        onClick={e => {
                            e.preventDefault()
                            setActive(id)
                            isProgrammaticScroll.current = true

                            const handleScrollEnd = () => {
                                isProgrammaticScroll.current = false

                                if(supportsScrollEnd) {
                                    document.removeEventListener('scrollend', handleScrollEnd)
                                } else {
                                    document.removeEventListener('scroll', handleScrollEnd)
                                }
                            }

                            if(supportsScrollEnd) {
                                document.addEventListener('scrollend', handleScrollEnd)
                            } else {
                                document.addEventListener('scroll', handleScrollEnd)
                            }

                            document.querySelector(`#${id}`).scrollIntoView({
                                behavior: 'smooth'
                            })
                        }}>
                        {(typeof text === 'string') && <Ellipsify text={text} />}
                        {(typeof text !== 'string') && text}
                    </Link>
                </NavigationItem>
            ))}
        </NavigationList>
    )
}

const NavigationSectionsContext = createContext()
NavigationSectionsContext.displayName = 'NavigationSections'

Navigation.Sections = ({ children }) => (
    <NavigationSectionsContext.Provider value={{}}>
        {children}
    </NavigationSectionsContext.Provider>
)

Navigation.Section = forwardRef(({ id, children, ...props }, ref) => {
    const [hasStickyMenu, setHasStickyMenu] = useState(false)

    useEffect(() => {
        setHasStickyMenu(!!document.querySelector('.sticky-menu'))
    }, [])

    return (
        <Section
            {...props}
            id={id}
            style={{ '--__huma-component-navigation-section-offset': `calc(var(--huma-global-main-layout-header-height)${hasStickyMenu ? ' + var(--huma-global-sticky-menu-height)' : ''} + 8px)` }}
            ref={ref}>
            {children}
        </Section>
    )
})

const useIntersectionObserver = ({
    setActive,
    rootMarginOffset,
    navigationItems = [],
    isProgrammaticScroll,
    headerOffset,
    stickyMenuOffset
}) => {
    const headingElementsRef = useRef({})
    const observerRef = useRef()

    useEffect(() => {
        const callback = headings => {
            // Skip updates during programmatic scroll
            if(isProgrammaticScroll.current) {
                return
            }

            // Check if the user is at the bottom of the page
            const isAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10

            if(isAtBottom) {
                setActive(navigationItems.at(-1).id)

                return
            }

            // Store all heading elements
            headingElementsRef.current = headings.reduce((map, headingElement) => {
                map[headingElement.target.id] = headingElement
                return map
            }, headingElementsRef.current)

            const visibleHeadings = []

            // Get all visible headings
            Object.keys(headingElementsRef.current).forEach((key) => {
                const headingElement = headingElementsRef.current[key]

                if(headingElement.isIntersecting) {
                    visibleHeadings.push(headingElement)
                }
            })

            if(visibleHeadings.length === 1) {
                setActive(visibleHeadings[0].target.id)
            } else if(visibleHeadings.length > 1) {
                // Find the heading that's closest to the top of the viewport
                const topOffset = headerOffset + stickyMenuOffset
                const closestHeading = visibleHeadings.reduce((closest, current) => {
                    const currentTop = current.target.getBoundingClientRect().top
                    const closestTop = closest.target.getBoundingClientRect().top

                    return Math.abs(currentTop - topOffset) < Math.abs(closestTop - topOffset) ? current : closest
                })

                setActive(closestHeading.target.id)
            }
        }

        // Disconnect previous observer if it exists
        if(observerRef.current) {
            observerRef.current.disconnect()
        }

        observerRef.current = new IntersectionObserver(callback, {
            rootMargin: `${(rootMarginOffset * -1) - 48}px 0px -40% 0px`,
            threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] // More granular threshold to trigger callback when a section just starting to enter
        })

        // Re-observe all sections
        const headingElements = Array.from(document.querySelectorAll('section[id]'))
        headingElements.forEach(element => observerRef.current.observe(element))

        return () => {
            if(observerRef?.current) {
                observerRef.current.disconnect()
            }
        }
    }, [setActive, rootMarginOffset, navigationItems, isProgrammaticScroll, headerOffset, stickyMenuOffset])
}

export const useNavigationSections = () => useContext(NavigationSectionsContext)
export default Navigation